
import React, { useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, RefreshCw, AlertCircle, Clock, CheckCircle, XCircle } from "lucide-react";
import { Client } from "../types";
import { EnrichmentResponse } from '@/hooks/useCompanyEnrichment';
import { formatDistanceToNow } from "date-fns";

interface ClientDetailsHeaderProps {
  client: Client;
  onEdit: () => void;
  onDelete: () => void;
  onEnrich: () => void;
  isEnriching: boolean;
  isPolling?: boolean;
  lastEnrichmentData?: Partial<EnrichmentResponse> | null;
  lastEnrichmentProgressStage?: string;
}

function parseIntSafely(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

function isValidDateString(value: unknown): value is string {
  return typeof value === 'string' && !isNaN(Date.parse(value));
}

export const ClientDetailsHeader: React.FC<ClientDetailsHeaderProps> = ({
  client,
  onEdit,
  onDelete,
  onEnrich,
  isEnriching,
  isPolling = false,
  lastEnrichmentData,
  lastEnrichmentProgressStage,
}) => {
  const enrichmentStatusBadge = useMemo(() => {
    const status = client.enrichment_status;
    const stage = lastEnrichmentProgressStage;
    
    if (isEnriching || isPolling || status === 'pending' || status === 'processing') {
      return <Badge variant="secondary" className="animate-pulse"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
    }
    
    if (status === 'available' || stage === 'completed') {
      return <Badge variant="outline" className="border-green-600 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Data Available</Badge>;
    }
    
    if (status === 'error' || stage?.startsWith('error_')) {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Error</Badge>;
    }
    
    if (status === 'none' || !status) {
      return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" />No Data</Badge>;
    }
    
    return <Badge variant="secondary">{status}</Badge>;
  }, [client.enrichment_status, isEnriching, isPolling, lastEnrichmentProgressStage]);

  const lastEnrichmentInfo = useMemo(() => {
    if (!lastEnrichmentData) return null;
    
    // Safely access created_at
    const createdAt = lastEnrichmentData.created_at;
    if (!isValidDateString(createdAt)) return null;
    
    const pollAttempts = parseIntSafely(lastEnrichmentData.poll_attempts);
    const timeAgo = formatDistanceToNow(new Date(createdAt), { addSuffix: true });
    
    return (
      <div className="text-xs text-muted-foreground">
        Last enrichment: {timeAgo}
        {pollAttempts > 0 && ` (${pollAttempts} attempts)`}
      </div>
    );
  }, [lastEnrichmentData]);

  return (
    <div className="flex items-center justify-between border-b pb-4">
      <div className="space-y-2">
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold">{client.name}</h1>
          <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
            {client.status || 'Unknown'}
          </Badge>
          {enrichmentStatusBadge}
        </div>
        
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          {client.industry && <span>Industry: {client.industry}</span>}
          {client.website && (
            <a 
              href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {client.website}
            </a>
          )}
        </div>
        
        {lastEnrichmentInfo}
      </div>
      
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onEnrich}
          disabled={isEnriching || isPolling}
          className="flex items-center"
        >
          {isEnriching || isPolling ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              {isPolling ? 'Polling...' : 'Enriching...'}
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Enrich Data
            </>
          )}
        </Button>
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="w-4 h-4 mr-2" />
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={onDelete} className="text-destructive hover:text-destructive">
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </Button>
      </div>
    </div>
  );
};
